import type { SessionType } from '@phi/authentication/frontend';
import {
    has_selected_document,
    in_tutorial,
    model_is_dirty,
    type State,
} from '@frontend/state';
import { check_that_user_subscription_is_valid } from '@frontend/user_authentication';
import { LOG, display_message_to_user } from '@phi/logging/frontend';
import { minutes, seconds } from '@phi/util';
import { get_core_side_dragging_state } from '@frontend/JCore/jcore';
import { save_phi_model_to_onshape, uid_save_model } from '@frontend/input_output';
import { type Ref, watch } from 'vue';
import { Interval } from '@phi/util/timeout';
import { keep_onshape_connection_alive } from '@phi/onshape/iframe-ipc';

export const onshape_autosave_factory = (state: State, session_type: Ref<Set<SessionType>>) => {
    function onshape_autosave(): Promise<void> {
        console.debug('Onshape autosave');
        console.log(`File save status: ${state.file_save_status}`);

        if (has_selected_document() && state.framework === 'onshape')
            keep_onshape_connection_alive(
                state.document!,
                state.workspace!,
                state.element!,
            );

        if (
            !state.loaded
            || state.currently_loading_model
            || in_tutorial()
            // simple test for if anything is loaded
            || uid_vertices.keys().length === 0
            // do not attempt while dragging
            || get_core_side_dragging_state() !== 'off'
        )
            return Promise.resolve(console.log('Autosave is not possible'));
        if (!model_is_dirty())
            return Promise.resolve(console.log('Model not dirty, skipping autosave'));
        if (!check_that_user_subscription_is_valid())
            return Promise.resolve(console.log('subscription not valid'));

        // If the activity timestamp has been exceeded by more than 30 minutes no do not attempt autosave
        const now = Date.now();
        if (now - state.activity_timestamp > minutes(30)) {
            display_message_to_user(LOG.INFO, 'Connection with Onshape will soon be interrupted due to inactivity.');
            return Promise.resolve();
        }

        return save_phi_model_to_onshape(
            uid_save_model(0, 1.0),
            true,
            false,
            false,
        );
    }

    const interval = new Interval(onshape_autosave, seconds(45));
    watch(
        () => session_type.value.has('onshape'),
        (value) => {
            if (value) {
                console.log('Autosave started');
                interval.start();
            } else {
                console.log('Interrupted autosave.');
                interval.stop();
            }
        },
        {
            immediate: true
        }
    );

    return onshape_autosave;
};
